/**
 * CanvasHotkeys
 *
 * Registers Option/Alt + 1…9 to switch the active canvas by tab position
 * (1 = default canvas, 2 = next, …). Mirrors the 1-based hash scheme in
 * `CanvasUrlSync` so the keyboard, the URL, and the tab strip all share
 * the same numbering.
 *
 * Also registers Option/Alt + Left/Right to move to the previous/next
 * canvas in the same sorted order. Wraps around the list — Right on the
 * last tab cycles back to the first, Left on the first goes to the last.
 *
 * Goes through `setActiveCanvasId` like a tab click would, so the URL
 * sync naturally pushes the matching `#c=N` entry. `react-hotkeys-hook`
 * already ignores key events fired inside inputs/textareas/contentEditable,
 * so typing in the chat composer won't accidentally switch canvases and
 * macOS's word-skip behaviour inside text fields stays intact.
 */

import { useCallback, useMemo } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useCanvases } from '../../contexts/CanvasContext';
import { compareCanvases } from '../../types/canvases';

const POSITION_HOTKEYS = ['alt+1', 'alt+2', 'alt+3', 'alt+4', 'alt+5', 'alt+6', 'alt+7', 'alt+8', 'alt+9'];

export function CanvasHotkeys() {
  const { canvases, activeCanvasId, setActiveCanvasId } = useCanvases();

  const sorted = useMemo(
    () => [...canvases.values()].sort(compareCanvases),
    [canvases],
  );

  const switchToPosition = useCallback(
    (oneBased: number) => {
      const target = sorted[oneBased - 1];
      if (!target) return;
      if (target.canvasId === activeCanvasId) return;
      setActiveCanvasId(target.canvasId);
    },
    [sorted, activeCanvasId, setActiveCanvasId],
  );

  const step = useCallback(
    (delta: 1 | -1) => {
      if (sorted.length < 2) return;
      const idx = sorted.findIndex((w) => w.canvasId === activeCanvasId);
      const from = idx === -1 ? 0 : idx;
      const next = (from + delta + sorted.length) % sorted.length;
      const target = sorted[next];
      if (!target || target.canvasId === activeCanvasId) return;
      setActiveCanvasId(target.canvasId);
    },
    [sorted, activeCanvasId, setActiveCanvasId],
  );

  useHotkeys(
    POSITION_HOTKEYS,
    (event, hk) => {
      event.preventDefault();
      const digit = hk.keys?.[0];
      const n = digit ? Number.parseInt(digit, 10) : NaN;
      if (!Number.isFinite(n) || n < 1 || n > 9) return;
      switchToPosition(n);
    },
    {
      description: 'Switch to canvas by position',
      metadata: { group: 'Canvas' },
      preventDefault: true,
    },
    [switchToPosition],
  );

  useHotkeys(
    'alt+left',
    (event) => {
      event.preventDefault();
      step(-1);
    },
    {
      description: 'Previous canvas',
      metadata: { group: 'Canvas' },
      preventDefault: true,
    },
    [step],
  );

  useHotkeys(
    'alt+right',
    (event) => {
      event.preventDefault();
      step(1);
    },
    {
      description: 'Next canvas',
      metadata: { group: 'Canvas' },
      preventDefault: true,
    },
    [step],
  );

  return null;
}
